import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { EnvironmentService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/environment_connect";
import type { NetworkMemberType } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/network_pb";
import { getEnvironmentNetworkInfoQueryKey } from "components/Environment/networkInfo";
import type { Environment } from "components/Environment/utils/types";
import { createConnectClient } from "server/connect";

const environmentClient = createConnectClient(EnvironmentService);

export function useEnvironmentNetworkMemberTypes(
  environments: readonly Environment[],
  enabled: boolean,
) {
  const targets = useMemo(
    () =>
      environments
        .map((environment) => ({
          cacheId: environment.workspaceId || environment.environment_id || "",
          environmentId:
            environment.environment_id || environment.workspaceId || "",
        }))
        .filter((target) => Boolean(target.cacheId && target.environmentId)),
    [environments],
  );
  const queries = useQueries({
    queries: targets.map((target) => ({
      queryKey: getEnvironmentNetworkInfoQueryKey(target.environmentId),
      queryFn: () =>
        environmentClient.getNetworkInfo({
          environmentId: target.environmentId,
        }),
      enabled,
      staleTime: 10_000,
    })),
  });

  const memberTypes = useMemo(
    () =>
      new Map<string, NetworkMemberType>(
        targets.flatMap((target, index) => {
          const memberType =
            queries[index]?.data?.networkInfo?.networkMemberType;
          return memberType === undefined
            ? []
            : ([[target.cacheId, memberType]] as const);
        }),
      ),
    [queries, targets],
  );
  const isSettled =
    !enabled ||
    queries.every(
      (query) =>
        query.fetchStatus === "idle" &&
        (query.status === "success" || query.status === "error"),
    );

  return { isSettled, memberTypes };
}
